import { HpDateValidator } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { createControlId } from '@avantia/questionset-model';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { HpDatePart, formatDate, getMonthDays, monthArray, setDatePart } from '../../../tools/dateTools';
import { HpClientStandardInputTextAndStyle } from '../../../types';
import { HpDateDropDownEventDetail, HpInputControlProps, HpOnMouseEvent } from '../../../types/inputControlTypes';
import { StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { handleKeyPressEvent } from '../common/basicDropDownListControl';
import { getMinMaxDateValuesFromValidator } from '../controlUtilities';

export interface HpDateDropDownControlProps extends HpInputControlProps {
  placeholder?: string;
  sortItemsBy?: string;
  title?: string;
  dayMonthYearKey: string;
  onClick: HpOnMouseEvent;
}

export interface HpDateDropDownState {
  dirty: boolean;
  value?: string;
}

type HpOrderByTypes = 'asc' | 'desc';

export function DateDropDownControl({
  answerSource,
  data,
  dataType,
  disabled,
  errors,
  formatType,
  id,
  onBlur,
  onChange,
  onClick,
  question,
  questionText,
  subtext,
  ui,
  value: answerValue
}: HpDateDropDownControlProps): JSX.Element {
  const error = errors ? errors[id] : undefined;
  const { textAndStyle: dataTextAndStyle, validation } = data;
  const textAndStyle = dataTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const { sortItemsBy, showDayDropdown = true } = textAndStyle;
  const value = answerValue as string;
  const [year = '', month = '', day = ''] = getDatePartsValues();
  const fullDate: string | undefined = tryGetFullDate();
  const dateFormat = 'YYYY-MM-DD';

  // Dropdowns state.
  const dayId: string = createControlId({ questionId: id, key: 'day' });
  const monthId: string = createControlId({ questionId: id, key: 'month' });
  const yearId: string = createControlId({ questionId: id, key: 'year' });

  // Validator.
  const dateValidator = validation.filter((v) => v.getType() === 'date')[0] as HpDateValidator;
  const { minValue, maxValue } = getMinMaxDateValuesFromValidator(dateValidator);
  const orderYearBy = getYearOrderBy(sortItemsBy);

  const days: JSX.Element[] = [];
  const months: JSX.Element[] = [];
  const years: JSX.Element[] = [];

  lodash
    .range(
      1,
      getMonthDays(
        fullDate
          ? new Date(fullDate)
          : new Date(
              parseInt(year.length ? year : '2000'),
              parseInt(month.length ? month : '0'),
              parseInt(day.length ? day : '10')
            )
      ) + 1
    )
    .forEach((day) => {
      const item = createDatePartInput('day', day);
      days.push(item);
    });

  monthArray.forEach((month) => {
    const item = createDatePartInput('month', month.key, month.name);
    months.push(item);
  });

  const yearRange: { start: number; end: number } = {
    start: minValue ? minValue.getFullYear() : 1890,
    end: (maxValue ? maxValue.getFullYear() : new Date().getFullYear()) + 1
  };

  lodash.range(yearRange.start, yearRange.end).forEach((year) => {
    const item = createDatePartInput('year', year);
    if (orderYearBy === 'asc') {
      years.push(item);
    } else {
      years.unshift(item);
    }
  });

  const datePartsInputs = (
    <div role="group">
      {showDayDropdown && (
        <DropdownButton
          id={dayId}
          key={dayId}
          title={getTitle('D', day, 'Day')}
          className="d-inline"
          disabled={disabled === true}
          variant={buttonVariant(day)}
          onBlur={handleBlur(dayId)}
          onKeyPress={handleKeyPressEvent}
          onSelect={handleSelect('day')}
        >
          {days}
        </DropdownButton>
      )}
      <DropdownButton
        id={monthId}
        key={monthId}
        title={getTitle('MMMM', month, 'Month')}
        className="d-inline"
        disabled={disabled === true}
        variant={buttonVariant(month)}
        onBlur={handleBlur(monthId)}
        onKeyPress={handleKeyPressEvent}
        onSelect={handleSelect('month')}
      >
        {months}
      </DropdownButton>
      <DropdownButton
        id={yearId}
        key={yearId}
        title={getTitle('YYYY', year, 'Year')}
        className="d-inline"
        disabled={disabled === true}
        variant={buttonVariant(year)}
        onBlur={handleBlur(yearId)}
        onKeyPress={handleKeyPressEvent}
        onSelect={handleSelect('year')}
      >
        {years}
      </DropdownButton>
    </div>
  );

  return (
    <>
      <StandardFormGroupWidget
        answerSource={answerSource}
        control={datePartsInputs}
        controlType="date-drop-down"
        dataType={dataType}
        error={error}
        helpInfo={data.helpInfo}
        helpInfoUI={ui.helpInfo}
        id={id}
        onClick={onChange}
        question={question}
        questionText={questionText}
        subtext={subtext}
        textAndStyle={textAndStyle}
      />
    </>
  );

  function handleBlur(id: string): ((event: React.FocusEvent<HTMLElement>) => void) | undefined {
    return ((e: any) => {
      const event = { ...e, currentTarget: document.getElementById(id) };
      onBlur(event, { type: 'date-drop-down', fullDate: value, question } as HpDateDropDownEventDetail);
    }) as any;
  }

  function handleSelect(datePart: HpDatePart): (eventKey: string | null, e: React.SyntheticEvent<unknown>) => void {
    return ((key: string, e: any) => {
      const updatedDate = buildDateFromDropDownValue(datePart, key);
      onClick(e, { type: 'date-drop-down', fullDate: updatedDate, question } as HpDateDropDownEventDetail);
    }) as any;
  }

  function createDatePartInput(
    datePartKey: HpDatePart,
    datePartValue: string | number,
    datePartTitle?: string | number
  ): JSX.Element {
    const itemKey = createControlId({ questionId: id, key: datePartValue.toString() });
    return (
      <Dropdown.Item
        value={datePartValue}
        as="button"
        id={createControlId({ questionId: id, key: `${datePartKey}${datePartValue.toString()}` })}
        onMouseDown={((e) => e.preventDefault()) as (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void}
        key={itemKey}
        onKeyPress={handleKeyPressEvent}
        eventKey={datePartValue.toString()}
        tabIndex={0}
      >
        {datePartTitle || datePartValue}
      </Dropdown.Item>
    );
  }

  function buildDateFromDropDownValue(datePart: HpDatePart, key: string): string {
    let updatedDate: string | Date;
    const datePartToUpdate = datePart === 'day' ? 'date' : datePart;
    if (fullDate) {
      updatedDate = formatDate(setDatePart(fullDate, datePartToUpdate, parseInt(key)), dateFormat);
      return updatedDate;
    }

    const prevAnswer = `${value}`;
    let [prevAnswerYear = '', prevAnswerMonth = '', prevAnswerDay = ''] = prevAnswer.split('-');
    switch (datePart) {
      case 'day':
        prevAnswerDay = key;
        break;
      case 'month':
        prevAnswerMonth = key;
        break;
      case 'year':
        prevAnswerYear = key;
        break;
      default:
        break;
    }

    if (prevAnswerMonth.length) {
      const daysInMonth = getMonthDays(
        new Date(parseInt(prevAnswerYear.length ? prevAnswerYear : '2000'), parseInt(prevAnswerMonth), 1)
      );

      if (parseInt(prevAnswerDay) >= daysInMonth) {
        prevAnswerDay = daysInMonth.toString();
      }
    }

    if (!showDayDropdown) {
      prevAnswerDay = '01';
    }

    updatedDate = `${prevAnswerYear}-${prevAnswerMonth}-${prevAnswerDay}`;
    return tryBuildFullDate(updatedDate) || updatedDate;
  }

  function getTitle(format: string, datePartValue: string, placeHolder: string): string {
    if (fullDate) {
      return formatDate(fullDate, format);
    } else if (!datePartValue.length) {
      return placeHolder;
    }

    const monthKey = parseInt(datePartValue);

    return format === 'MMMM'
      ? (monthArray.find((month) => month.key === monthKey) || { name: '' }).name
      : datePartValue;
  }

  function tryBuildFullDate(answer: string): string | undefined {
    const [year = '', month = '', day = ''] = answer.split('-');
    const isFullAnswer = year.length && month.length && day.length; // Can't check if date is valid as 12-01- is a valid date.
    if (isFullAnswer) {
      const fullDate = new Date(parseInt(year), parseInt(month), parseInt(day));
      return formatDate(fullDate, dateFormat);
    }
  }

  function getDatePartsValues(): string[] {
    const [year = '', month = '', day = ''] = value.split('-');
    if (year.length && month.length && day.length) {
      const parsedMonth = (parseInt(month) - 1).toString();
      return [year, parsedMonth, day];
    }

    return [year, month, day];
  }

  function tryGetFullDate(): string | undefined {
    return year.length && month.length && day.length ? value : tryBuildFullDate(value);
  }

  function getYearOrderBy(sortItemsBy?: string): HpOrderByTypes {
    const defaultOrderBy: HpOrderByTypes = 'desc';
    if (!sortItemsBy) {
      return defaultOrderBy;
    }

    const [datePart, orderBy] = sortItemsBy.split(':').map((part) => part.trim().toLocaleLowerCase());
    if (datePart !== 'year' || !isOrderByType(orderBy as HpOrderByTypes)) {
      throw new Error("The value provided for 'sortItemsBy' is not valid.");
    }

    return orderBy as HpOrderByTypes;
  }

  function isOrderByType(orderBy: HpOrderByTypes): orderBy is HpOrderByTypes {
    const orderByTypesArray: HpOrderByTypes[] = ['asc', 'desc'];
    return orderByTypesArray.includes(orderBy);
  }
}

function buttonVariant(value: string): string {
  return value.length ? 'primary' : 'outline-primary';
}
